// Sugalarm — portrait trend overlay (cycle 142; reworked cycle 156).
//
// The bare-state Now screen (no rapid-acting IOB) drops the always-on
// chart; this dismissible overlay is how the trend stays one tap away.
// Opened by tapping the stats strip; shows the same <HistoryChart> the
// portrait chart card uses plus the shared TIR/avg/lows footer.
//
// Cycle 156: the overlay is a deliberate analysis surface. It owns its
// OWN window state, seeded to 24h on every open and decoupled from the
// Now screen's `chartHours` (opening/closing the sheet never changes
// the inline chart's window). All six windows render as an orange pill
// row across the top — the cycle-142 pill→popover and the
// swipe-on-plot window stepper are gone, because the plot now carries
// the cycle-65 gestures: pinch-to-zoom + single-finger pan
// (`pinchZoom` + `panSliceMs` seeded to the selected window's full
// span, so the sheet opens showing the whole window and pinch-out
// zooms in). Data reuses the Now screen's already-loaded series when
// the selected window fits inside it; only a window the screen hasn't
// loaded fetches, through the same api wrappers the screen uses — no
// new endpoint or param.
//
// Mobile-portrait only; mounted only from the bare-state strip. The
// caller closes it whenever an alarm episode goes live so an alarm
// control can never be covered by the scrim (the safety invariant).

import { useEffect, useLayoutEffect, useRef, useState } from "react";

import * as alertsApi from "../../api/alerts";
import * as glucoseApi from "../../api/glucose";
import * as treatmentsApi from "../../api/treatments";
import type { Alert, Reading, Thresholds, Treatment } from "../../lib/types";
import { displayValue } from "../../lib/glucose";
import { glucoseWindowStats } from "../../lib/glucoseStats";
import { hapticSelect } from "../../lib/haptics";
import { BASAL_LOOKBACK_HOURS } from "../../lib/iob";
import { HistoryChart } from "./HistoryChart";
import styles from "./TrendSheet.module.css";

// The overlay always opens at 24h, regardless of the Now chart's window.
const DEFAULT_SHEET_HOURS = 24;
const HOUR_MS = 60 * 60 * 1000;

interface Props {
  open: boolean;
  onClose: () => void;
  /** The Now screen's already-loaded series. Readings + alerts cover
   *  `screenHours` back; treatments cover at least the cycle-135 basal
   *  lookback floor (48h). Reused whenever the overlay's selected
   *  window fits inside that coverage. */
  readings: Reading[];
  treatments: Treatment[];
  alerts: Alert[];
  /** The Now screen's `chartHours` — the span the series props cover.
   *  Read-only here: the overlay never drives it. */
  screenHours: number;
  thresholds: Thresholds;
  units: "mg/dL" | "mmol/L";
  h24: boolean;
  nowMs: number;
  windowChoices: readonly number[];
  windowLabel: (h: number) => string;
  live: boolean;
  onReadingTap?: (r: Reading, anchorRect: DOMRect) => void;
}

export function TrendSheet({
  open,
  onClose,
  readings,
  treatments,
  alerts,
  screenHours,
  thresholds,
  units,
  h24,
  nowMs,
  windowChoices,
  windowLabel,
  live,
  onReadingTap,
}: Props) {
  const plotRef = useRef<HTMLDivElement | null>(null);
  const [plotHeight, setPlotHeight] = useState(240);

  // The overlay's own window — seeded to 24h on every open.
  const [hours, setHours] = useState<number>(DEFAULT_SHEET_HOURS);
  useEffect(() => {
    if (open) setHours(DEFAULT_SHEET_HOURS);
  }, [open]);

  // Measured-fill plot height (same pattern as the Now screen's
  // chartContainer measurement).
  useLayoutEffect(() => {
    if (!open) return;
    const el = plotRef.current;
    if (!el) return;
    const measure = () => {
      const h = el.clientHeight;
      if (h > 0) setPlotHeight(h);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [open]);

  // Escape dismisses (web).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        hapticSelect();
        onClose();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Which series the screen's props already cover. Readings + alerts
  // are fetched `screenHours` back; treatments are floored at the
  // basal lookback (cycle 135), so the 24h default usually only needs
  // readings + alerts when the screen sits on a short window.
  const needReadings = hours > screenHours;
  const needTreatments = hours > Math.max(screenHours, BASAL_LOOKBACK_HOURS);
  const needAlerts = hours > screenHours;
  const needsFetch = needReadings || needTreatments || needAlerts;

  const [fetched, setFetched] = useState<{
    readings: Reading[];
    treatments: Treatment[];
    alerts: Alert[];
  }>({ readings: [], treatments: [], alerts: [] });

  // Freshness key — the screen's fetch effect re-runs per new reading;
  // keying on the newest loaded reading keeps the overlay's wider
  // window in step without polling on the 15s `nowMs` tick.
  const newestId = readings[readings.length - 1]?.id ?? null;

  useEffect(() => {
    if (!open || !needsFetch) return;
    let cancelled = false;
    (async () => {
      // Same wrappers + limits the Now screen's chart fetch uses
      // (cycle 51 sizing) — only the series the props don't cover.
      const [g, t, a] = await Promise.all([
        needReadings ? glucoseApi.history(hours, 30_000) : null,
        needTreatments ? treatmentsApi.list(hours, 10_000) : null,
        needAlerts ? alertsApi.list(hours, 5_000) : null,
      ]);
      if (cancelled) return;
      setFetched((prev) => ({
        readings:
          g?.ok === true
            ? [...g.data.readings].sort(
                (x, y) =>
                  new Date(x.timestamp).getTime() -
                  new Date(y.timestamp).getTime(),
              )
            : prev.readings,
        treatments: t?.ok === true ? t.data.treatments : prev.treatments,
        alerts: a?.ok === true ? a.data.alerts : prev.alerts,
      }));
    })();
    return () => {
      cancelled = true;
    };
  }, [open, hours, needsFetch, needReadings, needTreatments, needAlerts, newestId]);

  if (!open) return null;

  const rangeEndMs = nowMs;
  const rangeStartMs = rangeEndMs - hours * HOUR_MS;
  const sheetReadings = needReadings ? fetched.readings : readings;
  const sheetTreatments = needTreatments ? fetched.treatments : treatments;
  const sheetAlerts = needAlerts ? fetched.alerts : alerts;

  // Footer stats over the overlay's own window (shared lib — the same
  // definition the portrait/bare/landscape strips read).
  const windowed = sheetReadings.filter((r) => {
    const t = new Date(r.timestamp).getTime();
    return t >= rangeStartMs && t <= rangeEndMs;
  });
  const stats = glucoseWindowStats(windowed, thresholds);

  const pick = (h: number) => {
    if (h !== hours) {
      hapticSelect();
      setHours(h);
    }
  };

  const tirColor =
    stats.tir == null
      ? "var(--ink)"
      : stats.tir >= 70
        ? "var(--zone-ok)"
        : stats.tir >= 50
          ? "var(--zone-warn)"
          : "var(--zone-urgent)";

  return (
    <div
      className={styles.scrim}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          hapticSelect();
          onClose();
        }
      }}
    >
      <div
        className={styles.sheet}
        role="dialog"
        aria-modal="true"
        aria-label="Glucose trend"
      >
        <div className={styles.head}>
          <div
            className={styles.pillRow}
            role="group"
            aria-label="Chart window"
          >
            {windowChoices.map((h) => (
              <button
                key={h}
                type="button"
                aria-pressed={h === hours}
                className={`${styles.winPill} ${
                  h === hours ? styles.winPillActive : ""
                }`}
                onClick={() => pick(h)}
              >
                {windowLabel(h)}
              </button>
            ))}
          </div>
          {live ? (
            <span className={styles.live}>
              <span className={styles.liveDot} />
              live
            </span>
          ) : (
            <span className={styles.live} />
          )}
          <button
            type="button"
            className={styles.closeBtn}
            aria-label="Close trend"
            onClick={() => {
              hapticSelect();
              onClose();
            }}
          >
            ×
          </button>
        </div>
        <div ref={plotRef} className={styles.plot}>
          <HistoryChart
            readings={sheetReadings}
            treatments={sheetTreatments}
            alerts={sheetAlerts}
            thresholds={thresholds}
            rangeStartMs={rangeStartMs}
            rangeEndMs={rangeEndMs}
            height={plotHeight}
            units={units}
            h24={h24}
            nowMs={nowMs}
            showNowLine
            panSliceMs={hours * HOUR_MS}
            pinchZoom
            onReadingTap={onReadingTap}
            showTooltips
            partitionLabel={windowLabel(hours)}
          />
        </div>
        <div className={styles.statsStrip}>
          <div className={styles.stat}>
            <span className={styles.statNum} style={{ color: tirColor }}>
              {stats.tir == null ? "—" : `${stats.tir}%`}
            </span>
            <span className={styles.statLabel}>in range</span>
          </div>
          <div className={styles.stat}>
            <span className={styles.statNum}>
              {stats.avg == null ? "—" : displayValue(stats.avg, units)}
            </span>
            <span className={styles.statLabel}>avg</span>
          </div>
          <div className={styles.stat}>
            <span className={styles.statNum}>{stats.lows}</span>
            <span className={styles.statLabel}>
              {stats.lows === 1 ? "low" : "lows"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
